// ---------------------------------------------------------------------------
// geoSanitiser.js
// Strips country codes, country names, and geo trailing phrases from strings.
// ---------------------------------------------------------------------------

const GEO_REPLACEMENTS = [
  // Trailing geo lists like "UK,CA,AUS,Ingredients,Official Website" at end of string
  [/[\s,]+(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB)(\s*,\s*(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB|\w[\w\s]*))*\s*$/gi, ''],
  // Inline geo lists anywhere: "... UK,CA,AUS ..."
  [/\b(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB)(\s*,\s*(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB))+\b/g, ''],
  // Full country names
  [/\b(United Kingdom|United States(?: of America)?|Canada|Australia|New Zealand|Ireland|South Africa|India)\b/gi, ''],
  // Lone country codes (word-boundary protected)
  [/\b(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB)\b/g, ''],
];

/**
 * Remove all geo terms from a string.
 * Cleans up any double spaces, leading/trailing commas, and extra punctuation.
 */
export const stripGeo = (str) => {
  if (!str || typeof str !== 'string') return str;
  let out = str;
  for (const [pattern, replacement] of GEO_REPLACEMENTS) {
    out = out.replace(pattern, replacement);
  }
  return out
    .replace(/,\s*,/g, ',')
    .replace(/^\s*,+\s*/g, '')
    .replace(/\s*,+\s*$/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\(\(\s*\)\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/**
 * Sanitise ALL fields of an llmJson object — not just video title fields.
 * This is the nuclear option: strip geo from every string field.
 */
export const sanitiseAllLlmJsonFields = (llmJson) => {
  if (!llmJson) return llmJson;

  const scalarFields = [
    'subtitle', 'trailing', 'meta_description', 'meta_keywords',
    'og_description_head', 'twitter_description_head', 'breadcrumb_blurb',
    'video_title_date_product', 'main_video_description', 'json_ld_description',
    // Title fields (hyd_*_title) are the user's SEO title and stay as-is.
    'hyd_og_description', 'hyd_twitter_description',
  ];

  for (const field of scalarFields) {
    if (typeof llmJson[field] === 'string') {
      llmJson[field] = stripGeo(llmJson[field]);
    }
  }

  if (Array.isArray(llmJson.video_chapters)) {
    llmJson.video_chapters = llmJson.video_chapters.map(stripGeo);
  }
  if (Array.isArray(llmJson.card_titles)) {
    llmJson.card_titles = llmJson.card_titles.map(stripGeo);
  }
  if (Array.isArray(llmJson.card_contents)) {
    llmJson.card_contents = llmJson.card_contents.map(stripGeo);
  }

  return llmJson;
};

/**
 * Sanitise only fields used directly in video titles / schema names.
 * Kept for backward compat — internally calls sanitiseAllLlmJsonFields.
 */
export const sanitiseLlmJsonForVideo = (llmJson) => sanitiseAllLlmJsonFields(llmJson);

/**
 * Strip geo from an entire HTML string as a final pass.
 * Operates only on text nodes / attribute values — avoids breaking structure.
 * Uses a broad regex that catches any remaining geo term patterns.
 */
export const stripGeoFromHtml = (html) => {
  if (!html) return html;
  let out = html;

  // Pattern: "KEYWORD (subtitle) UK,CA,AUS,..." — the full trailing geo list
  // This catches the exact format seen in titles/og tags
  // Fix: Removed unnecessary escape on the opening bracket inside the character class
  out = out.replace(
    /\b(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB)(\s*,\s*(UK|US|USA|CA|AU|AUS|NZ|IE|ZA|IN|GB|\w[\w\s]*))*(?=\s*[\]"<[])/g,
    ''
  );

  // Broad sweep of all geo patterns
  for (const [pattern, replacement] of GEO_REPLACEMENTS) {
    out = out.replace(pattern, replacement);
  }

  // Clean up comma artefacts
  out = out
    .replace(/,\s*,/g, ',')
    .replace(/,\s*"/g, '"')
    .replace(/,\s*\]/g, ']')
    .replace(/,\s*\)/g, ')')
    .replace(/\(\s*,/g, '(')
    .replace(/\s{2,}/g, ' ');

  return out;
};